import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Paper, Grid, Chip, useTheme, alpha, SelectChangeEvent } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Warehouse as WarehouseIcon, LocalShipping as ShippingIcon } from '@mui/icons-material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { isExportShipment } from '../../../utils/exportShipment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { AppDispatch, RootStateType } from '../../../store';
import CreateShipmentContext from '../../../context/CreateShipment';
import {
  createShipmentContextType,
  PickupType,
  GetShipmentPayloadType,
  BulkShipmentType,
  PickupDataType,
} from '../../../types/shipment';
import SbButton from '../../../components/Button';
import Loader from '../../../components/Loader';
import PickupDrop from '../../../components/Shipment/PickupDrop';
import ShippingOptionSelect from '../../../components/ShippingOptionSelect/ShippingOptionSelect';
import { WarehouseBookingSection } from '../../../components/WarehouseBooking/WarehouseBookingApp';
import { getRates } from '../../../store/reducers/RateSummary';
import { getServiceList } from '../../../store/reducers/CarrierAndService';
import { placeBulkShipment } from '../../../store/reducers/Shipment/actions';
import { getShipmentPayload } from '../../../services/getShipmentPayload';
import { getWarehouseListForShipment } from '../../../store/reducers/Warehouse';
import { transformShipments } from '../../../utils/transformShipments';
import { fetchWarehouseBulkRates } from '../../../store/reducers/WarehouseBooking/warehouseBookingSlice';
import { SuccessErrorModalOpen, SuccessErrorModalReset } from '../../../store/reducers/SuccessErrorModal';
import useAccountLookup from '../../../hooks/useAccountLookup';
import { setSelectedUser, setReduxState } from '../../../store/reducers/AccountLookup';
import { RateObj } from '../../../store/reducers/RateSummary/Type';
import { DropdownOptionType } from '../../../components/Dropdown/Type';
import { validateShipmentFields } from './utils/validation';

export default function BatchServicesStep({ modalId = 'CreateShipment' }: { modalId?: string }): JSX.Element {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const {
    bulkShipments,
    setBulkShipments,
    batchPickupType,
    setBatchPickupType,
    carrierPreference,
    setCarrierPreference,
    userId,
    setActiveStep,
    rateAssignService,
    setRateAssignService,
  } = useContext(CreateShipmentContext) as createShipmentContextType;

  const { serviceDropdownList } = useSelector((state: RootStateType) => state.carrierAndService);
  const countryHashByIdRates = useSelector((state: RootStateType) => state.global.countriesObj);
  const { warehouseListForShipmentOptionsHashById, warehouseListForShipent } = useSelector(
    (state: RootStateType) => state.Warehouse,
  );
  const { shipmentSetting } = useSelector((state: RootStateType) => state?.orgSetup);
  const { profile } = useSelector((state: RootStateType) => state.loginUser);
  const { selectedUser } = useAccountLookup(modalId);
  const placementLoading = useSelector((state: RootStateType) => state.shipment.loading || false);

  const { pickupTimeSlots } = shipmentSetting || {};

  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const orgCountryName = organizationData?._country?.name?.trim() || organizationCountry?._country?.name?.trim() || '';

  const isAnyExportShipment = bulkShipments.some((s: BulkShipmentType) =>
    isExportShipment({
      orgCountryName,
      shipper: s.shipper,
      consignee: s.consignee,
    }),
  );

  const currentBatchDuty =
    bulkShipments.find((s: BulkShipmentType) =>
      isExportShipment({
        orgCountryName,
        shipper: s.shipper,
        consignee: s.consignee,
      }),
    )?.dutyCharge || 'DDU';

  const handleBatchDutyChargeChange = (type: 'DDU' | 'DDP') => {
    setBulkShipments((prev: BulkShipmentType[]) =>
      prev.map((s: BulkShipmentType) => {
        const isExp = isExportShipment({
          orgCountryName,
          shipper: s.shipper,
          consignee: s.consignee,
        });
        if (isExp) {
          return {
            ...s,
            dutyCharge: type,
            package: {
              ...s.package,
              dutyCharge: type,
            },
          };
        }
        return s;
      }),
    );
  };

  const [carrierPreferenceList, setCarrierPreferenceList] = useState<any[]>([]);
  const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [warehouseFetched, setWarehouseFetched] = useState(false);

  const fetchRates = async (systemId?: string[]) => {
    try {
      const systemValue: string[] =
        name === 'Cargo Solutions Network' ? systemId || [] : selectedUser?.systems || profile?._systems || [];

      const accountNumber =
        selectedUser?.accountNumber || selectedUser?._accountId?.accountNumber || profile?.accountNumber || '';

      const importRes = await dispatch(
        getRates({
          navigatedDate: batchPickupType.date?.toISOString() || dayjs().toISOString(),
          accountNumber,
          pageType: 'contract',
          csp: [],
          rateType: [],
          system: systemValue,
          isImport: false,
        }),
      );

      const exportRes = await dispatch(
        getRates({
          navigatedDate: batchPickupType.date?.toISOString() || dayjs().toISOString(),
          accountNumber,
          pageType: 'contract',
          csp: [],
          rateType: [],
          system: systemValue,
          isImport: true,
        }),
      );

      let importList: RateObj[] = [];
      let exportList: RateObj[] = [];

      if (importRes.type.endsWith('/fulfilled') && importRes.payload && 'data' in importRes.payload) {
        importList = (importRes.payload as any).data.list;
      }

      if (exportRes.type.endsWith('/fulfilled') && exportRes.payload && 'data' in exportRes.payload) {
        exportList = (exportRes.payload as any).data.list;
      }

      setRateAssignService([...importList, ...exportList]);
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  };

  useEffect(() => {
    dispatch(getServiceList({}));
  }, [dispatch]);

  useEffect(() => {
    if (name !== 'Cargo Solutions Network') {
      fetchRates();
    }
  }, [name, selectedUser, profile, batchPickupType.date]);

  useEffect(() => {
    if (name === 'Cargo Solutions Network' && !warehouseFetched) {
      dispatch(getWarehouseListForShipment());
      setWarehouseFetched(true);
    }

    if (name === 'Cargo Solutions Network' && bulkShipments.length > 0 && !selectedSystem && !selectedCarrier) {
      const result = transformShipments(bulkShipments as never, selectedUser?._id || profile?._id || '');
      if (result.data.length > 0) {
        dispatch(fetchWarehouseBulkRates(result.data)).then((resultRes: any) => {
          if (resultRes.payload?.aggregatedRates.length === 0 && resultRes.payload?.processedShipments === 0) {
            dispatch(
              SuccessErrorModalOpen({
                type: 'error',
                subTitle: 'No rates found for this query!',
                title: 'Try Again!',
              }),
            );
          }
        });
      }
    }
  }, [bulkShipments, warehouseFetched, name, selectedUser, profile, selectedSystem, selectedCarrier, dispatch]);

  useEffect(() => {
    const validCSPs = new Set(rateAssignService.map(r => r.csp));
    const filteredServices = serviceDropdownList.filter((service: any) => validCSPs.has(service.value));

    const defaultCarrierPreference = [
      { display: 'Fastest', value: 'fastest' },
      { display: 'Cheapest', value: 'cheapest' },
    ];

    if (name === 'Cargo Solutions Network') {
      setCarrierPreferenceList([...defaultCarrierPreference, ...filteredServices]);
    } else if (filteredServices.length) {
      setCarrierPreferenceList([
        ...defaultCarrierPreference,
        { display: 'Others', value: 'other', disable: true },
        ...filteredServices,
      ]);
    } else {
      setCarrierPreferenceList(defaultCarrierPreference);
    }
  }, [name, serviceDropdownList, batchPickupType, selectedUser, rateAssignService]);

  useEffect(() => {
    if (selectedUser) {
      dispatch(setReduxState({ modalId: 'createBulkShipment' }));
      dispatch(setSelectedUser(['createBulkShipment', selectedUser]));
    }
  }, [selectedUser, dispatch]);

  const getSystemIdFromWarehouse = (selectedWarehouseId: string[], warehouseList: any[]): string[] | undefined => {
    const matchedWarehouse = warehouseList.find(warehouse => warehouse._id === selectedWarehouseId[0]);
    return matchedWarehouse?._systemId;
  };

  const handleWarehouseChange = (
    _selected: Array<string>,
    _selectedObj: DropdownOptionType[],
    _event: SelectChangeEvent<string>,
  ) => {
    const systemId = getSystemIdFromWarehouse(_selected, warehouseListForShipent?.list || []);
    if (systemId) {
      fetchRates(systemId);
    }
    const [selectedWearhouse] = _selected;
    if (selectedWearhouse) {
      const selectedWarehouse = warehouseListForShipmentOptionsHashById[selectedWearhouse];
      const coAttention = selectedWarehouse?.coAttention;
      const [telephoneCode, number] = coAttention?.contact || ['', ''];
      const countryId = selectedWarehouse?._addressId?.country;
      const warehouseAddress = {
        ...selectedWarehouse?._addressId,
        country: countryHashByIdRates?.[countryId]?.name || '',
        name: selectedWarehouse?.name,
        phone: { telephoneCode, number, name: coAttention?.name },
        email: coAttention?.email,
      };

      setBatchPickupType((prev: PickupDataType) => ({
        ...prev,
        warehouse: selectedWearhouse,
        selectedWearhouseAddress: warehouseAddress,
      }));
    }
  };

  const handlePickupTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBatchPickupType((prev: PickupDataType) => ({ ...prev, type: value as PickupType }));
  };

  const handleTimeSlotChange = (
    _selected: Array<string>,
    _selectedObj: DropdownOptionType[],
    event: SelectChangeEvent<string>,
  ) => {
    const value = event.target.value;
    setBatchPickupType((prev: PickupDataType) => ({ ...prev, timeSlot: value }));
  };

  const handlePickupDateChange = (date: dayjs.Dayjs) => {
    setBatchPickupType((prev: PickupDataType) => ({ ...prev, date: date }));
  };

  const handleShippingSelect = (pref: string) => {
    setBulkShipments((prev: BulkShipmentType[]) =>
      prev.map((s: BulkShipmentType) => {
        if (s.isSelected) {
          return { ...s, carrierPreference: pref };
        }
        return s;
      }),
    );
    setCarrierPreference(pref);
  };

  const handleBook = async (carrierName: string, systemName: string, warehouseID: string) => {
    setSelectedCarrier(carrierName);
    setSelectedSystem(systemName);

    if (warehouseID) {
      const selectedWarehouse = warehouseListForShipmentOptionsHashById[warehouseID];
      const coAttention = selectedWarehouse?.coAttention;
      const [telephoneCode, number] = coAttention?.contact || ['', ''];
      const countryId = selectedWarehouse?._addressId?.country;
      const warehouseAddress = {
        ...selectedWarehouse?._addressId,
        country: countryHashByIdRates?.[countryId]?.name || '',
        name: selectedWarehouse?.name,
        phone: { telephoneCode, number, name: coAttention?.name },
        email: coAttention?.email,
      };

      setBatchPickupType((prev: PickupDataType) => ({
        ...prev,
        warehouse: warehouseID,
        selectedWearhouseAddress: warehouseAddress,
      }));
    }
  };

  const handlePlaceBulkShipment = async () => {
    const orgSlots = pickupTimeSlots?.slots?.length;

    if (batchPickupType.type === 'dropOff' && !batchPickupType.warehouse) {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle:
            name === 'Cargo Solutions Network'
              ? 'Please select an Injection Warehouse.'
              : 'Please choose the drop-off warehouse.',
        }),
      );
      return;
    } else if (
      batchPickupType.type === 'pickUp' &&
      (!batchPickupType.date || (orgSlots && !batchPickupType.timeSlot))
    ) {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle: 'Please choose a pickup date and time slot.',
        }),
      );
      return;
    }

    const isDropOff = batchPickupType.type === 'dropOff';

    let pickupDate: string, timeSlot: string;
    const warehouseId = batchPickupType.warehouse;
    const warehouseAddress = batchPickupType.selectedWearhouseAddress;

    let payload: GetShipmentPayloadType = { shipments: bulkShipments, isDropOff };

    payload.shipments = payload.shipments.map((shipment: any) => {
      shipment.package.pickupDate = batchPickupType.date
        ? dayjs(batchPickupType.date).toISOString()
        : dayjs().toISOString();

      const { carrierPreference: pref, ...rest } = shipment;

      if (pref === 'fastest' || pref === 'cheapest') {
        return shipment;
      }

      return {
        ...rest,
        package: shipment.package,
        selectedCourierId: pref,
      };
    });

    if (!isDropOff) {
      pickupDate = dayjs(batchPickupType?.date).toISOString() || dayjs().toISOString();
      timeSlot = batchPickupType.timeSlot;
      payload = { ...payload, pickupDate, timeSlot };
    }

    try {
      const res = await dispatch(
        placeBulkShipment([getShipmentPayload({ ...payload, warehouseId, warehouseAddress }), userId]),
      );

      if (res.payload && 'msg' in (res.payload as any)) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            subTitle: (res.payload as any).msg || 'Bulk shipment created',
            title: 'Success',
            onConfirm: () => {
              navigate('/shipment/bulk-summary');
              dispatch(SuccessErrorModalReset());
            },
          }),
        );
      }
    } catch (err) {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          subTitle: 'Something went wrong',
          title: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    if (batchPickupType.warehouse && selectedCarrier && selectedSystem) {
      handlePlaceBulkShipment();
    }
  }, [batchPickupType.warehouse, selectedCarrier, selectedSystem]);

  const isStep1Disabled = (): boolean => {
    if (
      bulkShipments.length === 0 ||
      !bulkShipments.every((s: BulkShipmentType) => {
        const val = validateShipmentFields(s);
        return (
          s.status !== 'pending' &&
          s.status !== 'processing' &&
          s.status !== 'failed' &&
          s.status !== 'error' &&
          val.isValid
        );
      })
    ) {
      return true;
    }

    const orgSlots = pickupTimeSlots?.slots?.length;
    if (batchPickupType.type === 'dropOff' && !batchPickupType.warehouse) return true;
    if (batchPickupType.type === 'pickUp' && (!batchPickupType.date || (orgSlots && !batchPickupType.timeSlot)))
      return true;

    if (name !== 'Cargo Solutions Network' && !carrierPreference) return true;
    return false;
  };

  return (
    <Loader loading={placementLoading || false} overly>
      <Box sx={{ width: '100%', py: 1 }}>
        {/* Step 1 Header Banner */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.15),
            background:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.dark, 0.15)
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
                    theme.palette.primary.light || theme.palette.primary.main,
                    0.08,
                  )} 100%)`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  label="Step 2 of 2"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: '6px',
                    height: '24px',
                  }}
                />
                <Chip
                  icon={
                    <CheckCircleIcon
                      sx={{ fontSize: '14px !important', color: `${theme.palette.success.main} !important` }}
                    />
                  }
                  label={`${bulkShipments.length} Shipments Verified`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: '24px', fontSize: '0.75rem', fontWeight: 600 }}
                />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
                Logistics & Service Selection
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Choose your dispatch method and carrier service rate preference applied across all{' '}
                {bulkShipments.length} verified bulk parcels.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Main Content Cards */}
        {name === 'Cargo Solutions Network' ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 2 }}>
              Warehouse & Service Selection
            </Typography>
            {selectedCarrier && selectedSystem && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip icon={<WarehouseIcon />} label={selectedSystem} color="primary" variant="outlined" />
                <Chip icon={<ShippingIcon />} label={selectedCarrier} color="secondary" variant="outlined" />
              </Box>
            )}
            <WarehouseBookingSection onClose={() => setActiveStep(0)} onBook={handleBook} />
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {/* Card 1: Dispatch Method (Pickup / Drop-off) */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.primary.main,
                    }}
                  >
                    <ShippingIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                      1. Dispatch Method (Pickup or Drop-off) *
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Select how parcels will be handed over to the courier
                    </Typography>
                  </Box>
                </Box>

                <Grid container item xs={12} lg={10} xl={8}>
                  <PickupDrop
                    handlePickupTypeChange={handlePickupTypeChange}
                    pickupType={batchPickupType.type}
                    handleTimeSlotChange={handleTimeSlotChange}
                    timeSlot={batchPickupType.timeSlot}
                    handlePickupDateChange={handlePickupDateChange}
                    pickupDate={batchPickupType.date}
                    warehouse={batchPickupType.warehouse}
                    handleWarehouseChange={handleWarehouseChange}
                  />
                </Grid>
              </Paper>
            </Grid>

            {/* Duty & Tax Billing (DDP / DDU) for Export Shipments */}
            {isAnyExportShipment && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3.5 },
                    borderRadius: '14px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.main,
                      }}
                    >
                      <ReceiptLongOutlinedIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                          Who pays duty & taxes? (DDP / DDU)
                        </Typography>
                        <Chip
                          label="Required"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Specify how import customs duties and destination taxes should be billed across export parcels
                      </Typography>
                    </Box>
                  </Box>

                  {/* Segmented DDP / DDU Selector */}
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 0.5,
                        bgcolor: alpha(theme.palette.divider, 0.08),
                        borderRadius: '10px',
                        gap: 1,
                      }}
                    >
                      <SbButton
                        variant={currentBatchDuty === 'DDU' ? 'contained' : 'text'}
                        onClick={() => handleBatchDutyChargeChange('DDU')}
                        sx={{
                          px: 3,
                          py: 1,
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          bgcolor: currentBatchDuty === 'DDU' ? 'primary.main' : 'transparent',
                          color: currentBatchDuty === 'DDU' ? 'common.white' : 'text.primary',
                          boxShadow:
                            currentBatchDuty === 'DDU' ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                          '&:hover': {
                            bgcolor:
                              currentBatchDuty === 'DDU' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                          },
                        }}
                      >
                        DDU (Delivered Duty Unpaid)
                      </SbButton>
                      <SbButton
                        variant={currentBatchDuty === 'DDP' ? 'contained' : 'text'}
                        onClick={() => handleBatchDutyChargeChange('DDP')}
                        sx={{
                          px: 3,
                          py: 1,
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          bgcolor: currentBatchDuty === 'DDP' ? 'primary.main' : 'transparent',
                          color: currentBatchDuty === 'DDP' ? 'common.white' : 'text.primary',
                          boxShadow:
                            currentBatchDuty === 'DDP' ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                          '&:hover': {
                            bgcolor:
                              currentBatchDuty === 'DDP' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                          },
                        }}
                      >
                        DDP (Delivered Duty Paid)
                      </SbButton>
                    </Box>

                    {/* Helper text under selector */}
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={currentBatchDuty === 'DDP' ? 'Added to your charges' : 'Consignee pays on delivery'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          bgcolor:
                            currentBatchDuty === 'DDP'
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.success.main, 0.12),
                          color: currentBatchDuty === 'DDP' ? 'primary.main' : 'success.dark',
                        }}
                      />
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {currentBatchDuty === 'DDP'
                          ? 'Import duty & taxes are added to your invoice and prepaid. Consignee pays nothing upon delivery.'
                          : 'Import duty & taxes will be collected directly from the consignee upon delivery.'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Card 2: Carrier & Service Selection */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.primary.main,
                    }}
                  >
                    <WarehouseIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                      2. Carrier & Service Selection *
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Choose carrier service rate preference applied across all bulk parcels
                    </Typography>
                  </Box>
                </Box>

                <Grid container item xs={12} md={6} lg={5}>
                  <ShippingOptionSelect
                    required={name === 'Cargo Solutions Network'}
                    shippingOptions={carrierPreferenceList}
                    onSelect={handleShippingSelect}
                  />
                </Grid>

                {carrierPreference !== 'cheapest' && carrierPreference && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 3,
                      p: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                      borderRadius: '10px',
                    }}
                  >
                    <img src="/images/shipment/warning.svg" alt="warning" height={22} width={22} />
                    <Typography
                      variant="body2"
                      sx={{ ml: 1.5, color: theme.palette.warning.dark || '#b78103', fontWeight: 500 }}
                    >
                      Custom carrier preference is selected. Cheapest is usually recommended for bulk dispatches.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Placement Footer Card */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <SbButton variant="outlined" onClick={() => setActiveStep(0)} startIcon={<ArrowBackIcon />}>
                  Back to Review
                </SbButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, display: { xs: 'none', sm: 'block' } }}
                  >
                    Total: <strong>{bulkShipments.length} Shipments</strong>
                  </Typography>
                  <SbButton
                    variant="contained"
                    onClick={handlePlaceBulkShipment}
                    disabled={isStep1Disabled()}
                    sx={{
                      py: 1.2,
                      px: 3.5,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      borderRadius: '10px',
                      boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                    }}
                  >
                    Place {bulkShipments.length} Shipments
                  </SbButton>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </Loader>
  );
}
